import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

/** Shared design tokens + primitives for the ServiceBuds marketing / preview-access pages. */
export const T = {
  accent: '#16857A',
  accentDark: '#0E3F3A',
  hero: 'linear-gradient(135deg,#15524C 0%,#0E3F3A 58%,#0B342F 100%)',
  accentShadow: 'rgba(14,63,58,.34)',
  canvas: '#EAEEEF',
  surface: '#FFFFFF',
  border: '#E7ECEC',
  field: '#F2F5F5',
  fieldBorder: '#E5EAEA',
  text: '#14222B',
  text2: '#5E6E72',
  muted: '#8A98A0',
  danger: '#D8443F',
  dangerBg: '#FCE9E8',
  ok: '#1E9E73',
  warn: '#E0922F',
  warnBg: '#FBF1DD',
};

export const FONT =
  "'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif";

// ── Shell (brand nav + footer) ────────────────────────────────────────────────

export const LandingShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Page>
    <Nav>
      <Brand to="/"><Mark>◆</Mark> ServiceBuds</Brand>
      <NavRight>
        <NavItem to="/">Home</NavItem>
        <NavItem to="/products">Products</NavItem>
        <NavItem to="/about">About</NavItem>
        <NavItem to="/pay">Pricing</NavItem>
        <NavItem to="/signin">Sign in</NavItem>
        <NavCta to="/signup">Request access</NavCta>
      </NavRight>
    </Nav>
    {children}
    <Footer>
      <span>© ServiceBuds — the operations software suite.</span>
      <FootLinks>
        <NavItem to="/products">Products</NavItem>
        <NavItem to="/signin">Sign in</NavItem>
        <NavItem to="/team/requests">Team console</NavItem>
      </FootLinks>
    </Footer>
  </Page>
);

const Page = styled.div`
  min-height: 100%;
  background: ${T.canvas};
  color: ${T.text};
  font-family: ${FONT};
`;

const Nav = styled.nav`
  max-width: 1120px;
  margin: 0 auto;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const Brand = styled(Link)`
  display: flex; align-items: center; gap: 8px;
  font-size: 17px; font-weight: 800; letter-spacing: -.02em;
  color: ${T.text}; text-decoration: none;
`;
const Mark = styled.span`color: ${T.accent}; font-size: 18px;`;

const NavRight = styled.div`display: flex; align-items: center; gap: 6px; flex-wrap: wrap;`;

export const NavItem = styled(Link)`
  padding: 8px 12px; border-radius: 10px;
  font-size: 13.5px; font-weight: 600; text-decoration: none; color: ${T.text2};
  &:hover { color: ${T.text}; background: #E6F2F0; }
`;

const NavCta = styled(Link)`
  padding: 9px 15px; border-radius: 10px;
  font-size: 13.5px; font-weight: 700; text-decoration: none;
  color: #fff; background: ${T.accent};
  &:hover { background: ${T.accentDark}; }
`;

const Footer = styled.footer`
  max-width: 1120px; margin: 0 auto;
  padding: 28px 24px 44px;
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  border-top: 1px solid #E1E6E6;
  font-size: 13px; color: ${T.muted};
`;
const FootLinks = styled.div`display: flex; gap: 4px;`;

// ── Layout primitives ─────────────────────────────────────────────────────────

export const Container = styled.div`max-width: 1120px; margin: 0 auto; padding: 0 24px;`;
export const Narrow = styled.div`max-width: 640px; margin: 0 auto; padding: 0 24px;`;

export const Panel = styled.div`
  background: ${T.surface};
  border: 1px solid ${T.border};
  border-radius: 20px;
  box-shadow: 0 8px 24px rgba(13,52,47,.07);
  padding: 32px;
`;

export const Eyebrow = styled.span`
  display: block; font-size: 11.5px; font-weight: 700; letter-spacing: .07em;
  text-transform: uppercase; color: ${T.accent}; margin-bottom: 6px;
`;
export const H2 = styled.h2`
  font-size: 26px; font-weight: 800; letter-spacing: -.02em; color: ${T.text}; margin: 0 0 6px;
`;
export const Sub = styled.p`font-size: 14.5px; color: ${T.text2}; margin: 0; line-height: 1.55;`;

// ── Buttons ───────────────────────────────────────────────────────────────────

const primaryCss = css`
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 13px 20px; border: none; cursor: pointer; border-radius: 13px;
  font-family: ${FONT}; font-size: 15px; font-weight: 700; text-decoration: none;
  color: #fff; background: linear-gradient(135deg, ${T.accent} 0%, ${T.accentDark} 100%);
  box-shadow: 0 8px 18px ${T.accentShadow};
  transition: transform .12s ease, filter .16s ease;
  &:hover { filter: brightness(1.06); }
  &:active { transform: scale(.98); }
  &:disabled { opacity: .5; cursor: not-allowed; filter: none; }
`;
export const PrimaryButton = styled.button`${primaryCss}`;
export const PrimaryLink = styled(Link)`${primaryCss}`;

const ghostCss = css`
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 13px 20px; cursor: pointer; border-radius: 13px;
  font-family: ${FONT}; font-size: 15px; font-weight: 700; text-decoration: none;
  color: ${T.accentDark}; background: ${T.surface}; border: 1px solid ${T.border};
  transition: background .16s ease;
  &:hover { background: #F1F4F4; }
`;
export const GhostButton = styled.button`${ghostCss}`;
export const GhostLink = styled(Link)`${ghostCss}`;

// ── Form field ────────────────────────────────────────────────────────────────

export const Field = styled.label`
  display: flex; flex-direction: column; gap: 6px;
  .lbl { font-size: 12.5px; font-weight: 700; color: ${T.text}; }
  input, textarea, select {
    font-family: ${FONT}; font-size: 14.5px; color: ${T.text};
    background: ${T.field}; border: 1px solid ${T.fieldBorder}; border-radius: 12px;
    padding: 12px 14px; outline: none; width: 100%; box-sizing: border-box;
    transition: border-color .16s ease, box-shadow .16s ease;
  }
  textarea { resize: vertical; min-height: 84px; }
  input:focus, textarea:focus, select:focus {
    border-color: ${T.accent}; box-shadow: 0 0 0 3px rgba(22,133,122,.14);
  }
`;

export const ErrorText = styled.div`
  font-size: 13px; font-weight: 600; color: ${T.danger};
  background: ${T.dangerBg}; border-radius: 10px; padding: 8px 12px;
`;

interface BadgeProps { $tone: string; }
export const StatusBadge = styled.span<BadgeProps>`
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  padding: 4px 10px; border-radius: 20px;
  color: ${p => p.$tone === 'APPROVED' ? '#1B7A52' : p.$tone === 'REJECTED' ? '#C5363B' : '#9A6510'};
  background: ${p => p.$tone === 'APPROVED' ? '#E3F3EC' : p.$tone === 'REJECTED' ? '#FCE9E8' : '#FBF1DD'};
`;
